// Bots.tsx — the All bots screen (Task 10, handoff README §4). Rebuilt off one production
// screenshot (`docs/design/dashboard-redesign-brief/current-state/03-bots-list.png`): a
// three-up card grid with one small card in a 1440px viewport, whose only fact was
// "Created 8/8/2026" — the least useful thing anyone knows about a bot. The list holds 1–5
// items (Free/Starter allow one bot, only Growth's $79 tier allows five), so full-width rows
// replace the grid: they look intentional at any count from one to five, where a lone
// three-up card looks like a bug. Every column now answers "is it working, is there anything
// to deal with" — status, a 14-day sparkline + message count, unanswered count, source
// readiness. The creation date, the old card's one fact, appears nowhere.
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BotIcon, ChevronRightIcon } from 'lucide-react';
import { StatusChip } from '@repo/ui/components/status-chip';
import { Button } from '@repo/ui/components/button';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import { EmptyState } from '@repo/ui/components/empty-state';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import type { BotDto, BotListItemDto } from '@repo/shared-types';
import { PageHeader } from '../components/PageHeader';
import { useBillingSummary } from '../hooks/useBillingSummary';
import { useBotsWithHealth } from '../hooks/useBots';
import { apiFetch } from '../lib/api';
import { botCapGate, botsUsedLabel, rowState, sourceRollupLabel } from '../lib/bots-list';
import { sparklinePointsFromCounts } from '../lib/overview';
import { useLocale } from '../locales/context';
import type { Catalog } from '../locales/en';
import { formatNumber } from '../locales/format';

// handoff README §4's exact column grid: `minmax(0, 2.2fr) 92px 130px 92px 108px 24px`.
const ROW_GRID = 'grid-cols-[minmax(0,2.2fr)_92px_130px_92px_108px_24px]';

function BotMark({ name }: { name: string }) {
  return (
    <span
      className="font-display grid size-[34px] shrink-0 place-items-center rounded-[9px] text-[15px] font-bold text-primary"
      style={{ backgroundColor: 'color-mix(in oklch, var(--primary) var(--st-wash), transparent)' }}
    >
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

function TableHeaderRow({ t }: { t: Catalog }) {
  return (
    <div
      className={`grid h-[34px] items-center gap-4 bg-muted px-5 font-mono text-[10.5px] tracking-[0.06em] text-muted-foreground uppercase ${ROW_GRID}`}
    >
      <span>{t.bots.colBot}</span>
      <span>{t.bots.colStatus}</span>
      <span>{t.bots.colMessages14d}</span>
      <span>{t.bots.colUnanswered}</span>
      <span>{t.bots.colSources}</span>
      <span />
    </div>
  );
}

function BotRowSkeleton() {
  return (
    <div className={`grid h-[68px] items-center gap-4 border-t border-border px-5 ${ROW_GRID}`}>
      <div className="flex min-w-0 items-center gap-[11px]">
        <Skeleton className="size-[34px] shrink-0 rounded-[9px]" />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <SkeletonText className="w-[70%]" />
          <SkeletonText className="w-[45%]" />
        </div>
      </div>
      <Skeleton className="h-6 w-[70px] rounded-full" />
      <Skeleton className="h-[22px] w-[100px] rounded-md" />
      <SkeletonText className="w-8" />
      <SkeletonText className="w-16" />
      <span />
    </div>
  );
}

function BotRow({ bot }: { bot: BotListItemDto }) {
  // Fix-wave item 3: this used to open its own `['bots', bot.id, 'sources']` query per row
  // purely to derive the domain string — a client-side per-bot fan-out (5 parallel requests
  // for a Growth workspace, unbounded for an internal one) that spec §7.2 explicitly forbids
  // "on the client or in a server loop". `listWithHealth` already reads each bot's sources for
  // `worstStatus`/`sourceCount`; `health.domain` derives the same fact there, in the one
  // aggregate query the whole collection already pays for.
  const domain = bot.health.domain;
  const state = rowState(bot);
  const sparkColor = state === 'ready' ? 'var(--primary)' : 'var(--muted-foreground)';
  const points = sparklinePointsFromCounts(bot.health.spark, 60, 22);
  const unanswered = bot.health.unansweredGroups;
  const { t, locale } = useLocale();

  return (
    <Link
      to={`/bots/${bot.id}`}
      className={`grid h-[68px] items-center gap-4 border-t border-border px-5 transition-colors hover:bg-accent ${ROW_GRID}`}
    >
      <div className="flex min-w-0 items-center gap-[11px]">
        <BotMark name={bot.name} />
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold">{bot.name}</span>
          {domain && <span className="truncate font-mono text-[11.5px] text-muted-foreground">{domain}</span>}
        </span>
      </div>

      <StatusChip state={state} label={t.status[state]} className="justify-self-start" />

      <div className="flex items-center gap-[9px]">
        <svg viewBox="0 0 60 22" width={60} height={22} className="shrink-0 overflow-visible">
          <polyline
            points={points}
            fill="none"
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            stroke={sparkColor}
          />
        </svg>
        <span className="text-[13.5px] font-semibold">{formatNumber(bot.health.messages14d, locale)}</span>
      </div>

      <span
        className="text-[13.5px] font-semibold"
        style={{ color: unanswered ? 'var(--st-paused)' : 'var(--muted-foreground)' }}
      >
        {unanswered ? formatNumber(unanswered, locale) : '—'}
      </span>

      <span className="truncate text-[12.5px] text-muted-foreground">{sourceRollupLabel(bot.health, t)}</span>

      <ChevronRightIcon size={15} strokeWidth={2} className="text-muted-foreground" aria-hidden="true" />
    </Link>
  );
}

export function Bots() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // The health shape (Task 2/4) — every row on this screen needs its aggregates, unlike the
  // rail, which only ever needs the cheap `useBots()` list.
  const { data: bots, isPending, error } = useBotsWithHealth(true);
  const { data: billing } = useBillingSummary();

  // `cap === null` while billing hasn't loaded yet OR the workspace is internal — both cases
  // must never gate (billing.ts `?? null` fallback mirrors `BotSwitcher.tsx`'s own guard).
  const cap = billing?.plan.caps.bots ?? null;
  const gate = botCapGate(bots?.length ?? 0, cap);

  // Task 1 (dashboard-shell-feedback plan, 2026-08-09): `BotSwitcher.tsx`'s "New bot" row must
  // lead to actually creating a bot, not to this list. Rather than duplicating the create
  // dialog's mutation/state in the rail (mounted on every route), the switcher navigates here
  // with `?new=1` and this is the one place that opens it — same dialog, same mutation, same
  // "navigate to the new bot" success path either way. Guarded on `!gate.atCap` because the
  // switcher only ever produces this link when there's room. A stale/typed-in `?new=1` at the
  // cap does NOT reliably fall through to a disabled button: on the very first render `bots`
  // and `billing` are still `undefined`, so `gate.atCap` computes `false` and the dialog opens
  // anyway — harmless, since submitting just 402s, but worth stating accurately rather than
  // claiming a guarantee this guard doesn't provide.
  //
  // Opening the dialog is done during render ("adjusting state when a prop changes",
  // react.dev/learn/you-might-not-need-an-effect), not in a `useEffect`, per this project's
  // `react-hooks/set-state-in-effect` lint rule — and NOT via a `useRef` guard either (fix-wave
  // item 2's first attempt tried that; this project's `react-hooks/refs` rule forbids reading
  // OR writing a ref during render, only inside effects/handlers). `prevNewParam` is the
  // react.dev-endorsed shape for this exact case ("storing information from previous renders",
  // reference/react/useState#storing-information-from-previous-renders): state that mirrors the
  // param's own last-seen value, updated during render the moment it changes.
  //
  // Seeded to `null` UNCONDITIONALLY — NOT `useState(newParam)` (fix-wave item 2's second
  // attempt, also wrong, caught in re-review). `/bots` and `/bots/:botId` are sibling routes:
  // the switcher's `New bot` link is clicked from a bot-scoped page (Overview, Conversations,
  // wherever the switcher actually lives), so the OVERWHELMINGLY common path mounts this
  // component fresh with `?new=1` already in the URL, not already mounted and then navigated
  // within. Seeding `prevNewParam` from `newParam` made that first render see
  // `newParam === prevNewParam` (both `'1'`) and never open the dialog — the exact primary path
  // broken to fix the secondary one (retriggering while already on `/bots`, which needs the
  // component to still be mounted, e.g. clicking `New bot` again after Cancel). Hardcoding
  // `null` fixes both: a fresh mount with `?new=1` present is `null → '1'`, a genuine change,
  // same as a live retrigger while mounted — the two cases are no longer distinguishable by
  // this code, which is exactly the point. The URL cleanup effect below stripping `?new=1` back
  // to `null` right after handling it is what makes retriggering work at all: `prevNewParam`
  // observes that as a change too, resetting itself so the NEXT `'1'` reads as fresh again
  // rather than repeating one already "handled" — the original one-shot-guard bug fix-wave item
  // 2 first landed to fix, and the only thing this comment needs to not re-break by seeding
  // `prevNewParam` from the current value again. Guarded on `!gate.atCap` because the switcher
  // only ever produces this link when there's room. A stale/typed-in `?new=1` at the cap does
  // NOT reliably fall through to a disabled button: on the very first render `bots` and
  // `billing` are still `undefined`, so `gate.atCap` computes `false` and the dialog opens
  // anyway — harmless, since submitting just 402s, but worth stating accurately rather than
  // claiming a guarantee this guard doesn't provide.
  const newParam = searchParams.get('new');
  const [prevNewParam, setPrevNewParam] = useState<string | null>(null);
  if (newParam !== prevNewParam) {
    setPrevNewParam(newParam);
    if (newParam === '1' && !gate.atCap) {
      setFormError(null);
      setName('');
      setDialogOpen(true);
    }
  }

  // Only the URL cleanup (`setSearchParams`, a write to the router's own state, not this
  // component's) belongs in an effect: it is the "synchronize with an external system" case
  // `react-hooks/set-state-in-effect` exists to allow.
  useEffect(() => {
    if (newParam === '1') {
      setSearchParams(
        (prev) => {
          prev.delete('new');
          return prev;
        },
        { replace: true },
      );
    }
  }, [newParam, setSearchParams]);

  const createBot = useMutation({
    mutationFn: (payload: { name: string }) =>
      apiFetch<BotDto>('/api/bots', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
    onSuccess: async (bot) => {
      await queryClient.invalidateQueries({ queryKey: ['bots'] });
      setDialogOpen(false);
      setName('');
      setFormError(null);
      navigate(`/bots/${bot.id}`);
    },
    onError: (err: Error) => setFormError(err.message),
  });

  function openDialog() {
    setFormError(null);
    setName('');
    setDialogOpen(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    createBot.mutate({ name: name.trim() });
  }

  // Task 7 (dashboard-shell cycle): the zero-bot → `/onboarding` redirect now lives in
  // `LandingResolver` at `/`. `/bots` is its own path, so the empty state below is reachable
  // on purpose — visiting it directly (e.g. via this page's own nav item) no longer bounces.

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t.bots.pageTitle}
        subtitle={bots && botsUsedLabel(bots.length, cap, t)}
        action={
          <Button onClick={openDialog} disabled={gate.atCap} style={gate.atCap ? { opacity: 0.45 } : undefined}>
            {t.shell.newBot}
          </Button>
        }
      />

      {isPending && (
        <div className="overflow-x-auto rounded-[14px] border border-border bg-card shadow-el-1">
          <div className="min-w-[760px]">
            <TableHeaderRow t={t} />
            <BotRowSkeleton />
            <BotRowSkeleton />
          </div>
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error.message}</p>}

      {bots && bots.length === 0 && (
        <EmptyState
          icon={BotIcon}
          title={t.bots.emptyTitle}
          description={t.bots.emptyDescription}
          action={<Button onClick={openDialog}>{t.shell.newBot}</Button>}
        />
      )}

      {bots && bots.length > 0 && (
        <>
          {/* `overflow-x-auto` + inner min-width (2026-08-20): on phone widths the fixed-px
              grid columns collapsed into each other (header read "BOTSTATUS"); the table now
              scrolls sideways inside its own card instead. 760px min (not the grid's bare
              ~530px sum): since sideways scrolling exists anyway, the name column deserves
              real width — at 620 it squeezed to ~90px and truncated to one letter. */}
          <div className="overflow-x-auto rounded-[14px] border border-border bg-card shadow-el-1">
            <div className="min-w-[760px]">
              <TableHeaderRow t={t} />
              {bots.map((bot) => (
                <BotRow key={bot.id} bot={bot} />
              ))}
            </div>
          </div>

          {gate.showUpgradeGate && gate.upgrade && (
            <div className="flex items-center justify-between gap-4 rounded-xl border border-dashed border-border px-5 py-4">
              <div>
                <div className="text-[13.5px] font-semibold">
                  {t.bots.planIncludesBots({ cap: gate.cap ?? 0 })}
                </div>
                <div className="mt-0.5 text-[12.5px] text-muted-foreground">
                  {t.bots.upgradeGateDescription({
                    name: gate.upgrade.name,
                    priceUsd: gate.upgrade.priceUsd,
                    maxBots: gate.upgrade.maxBots,
                  })}
                </div>
              </div>
              <Link to="/billing" className="shrink-0 text-[13px] font-semibold hover:underline">
                {t.bots.comparePlansLink}
              </Link>
            </div>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.bots.newBotDialogTitle}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="bot-name">{t.bots.botNameFieldLabel}</Label>
              <Input
                id="bot-name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.bots.botNamePlaceholder}
              />
            </div>
            {formError && <p className="text-sm text-destructive">{formError}</p>}
            <DialogFooter>
              <Button type="submit" disabled={createBot.isPending || !name.trim()}>
                {createBot.isPending ? t.bots.creatingCta : t.bots.createCta}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
